import styled, { css } from 'styled-components';
import React, { ElementType } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'outlined' | 'outlined-grey';
type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  children: React.ReactNode;
  as?: ElementType;
  href?: string;
  target?: string;
  rel?: string;
  style?: React.CSSProperties;
}

interface StyledButtonProps {
  variant: ButtonVariant;
  size: ButtonSize;
  fullWidth: boolean;
}

const StyledButton = styled.button<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: ${({ theme }) => theme.transitions.fast};
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  ${({ fullWidth }) => fullWidth && css`
    width: 100%;
  `}
  
  /* Size Variants with responsive adjustments */
  ${({ size, theme }) =>
    size === 'small' &&
    css`
      padding: ${theme.spacing[1]} ${theme.spacing[2]};
      font-size: ${theme.fontSizes.xs};
      
      @media (min-width: ${theme.breakpoints.md}) {
        padding: ${theme.spacing[2]} ${theme.spacing[3]};
        font-size: ${theme.fontSizes.sm};
      }
    `}
    
  ${({ size, theme }) =>
    size === 'medium' &&
    css`
      padding: ${theme.spacing[2]} ${theme.spacing[3]};
      font-size: ${theme.fontSizes.sm};
      
      @media (min-width: ${theme.breakpoints.md}) {
        padding: ${theme.spacing[3]} ${theme.spacing[5]};
        font-size: ${theme.fontSizes.md};
      }
    `}
    
  ${({ size, theme }) =>
    size === 'large' &&
    css`
      padding: ${theme.spacing[3]} ${theme.spacing[4]};
      font-size: ${theme.fontSizes.md};
      
      @media (min-width: ${theme.breakpoints.md}) {
        padding: ${theme.spacing[4]} ${theme.spacing[6]};
        font-size: ${theme.fontSizes.lg};
        min-height: 58px;
      }
    `}
  
  /* Color Variants */
  ${({ variant, theme }) =>
    variant === 'primary' &&
    css`
      background: ${theme.colors.primary}; /* #4361EE */
      color: white;
      border: none;
      
      &:hover {
        background: #3D5BDB; /* Slightly darker shade of primary */
        box-shadow: 0 4px 12px rgba(67, 97, 238, 0.3);
        transform: translateY(-1px);
      }
      
      &:active {
        transform: translateY(0);
        background: #3751D1; /* Even darker on active */
        box-shadow: 0 2px 4px rgba(67, 97, 238, 0.2);
      }
      
      &:disabled {
        background: ${theme.colors.border};
        color: ${theme.colors.textLight};
        cursor: not-allowed;
        box-shadow: none;
        transform: none;
      }
    `}
    
  ${({ variant, theme }) =>
    variant === 'secondary' &&
    css`
      background: ${theme.colors.primary}; /* Using the same blue as primary */
      color: white;
      border: none;
      
      &:hover {
        background: #3D5BDB; /* Same hover color as primary */
        box-shadow: 0 4px 12px rgba(67, 97, 238, 0.3);
        transform: translateY(-1px);
      }
      
      &:active {
        transform: translateY(0);
        background: #3751D1; /* Same active color as primary */
        box-shadow: 0 2px 4px rgba(67, 97, 238, 0.2);
      }
      
      &:disabled {
        background: ${theme.colors.border};
        color: ${theme.colors.textLight};
        cursor: not-allowed;
        box-shadow: none;
        transform: none;
      }
    `}
    
  ${({ variant, theme }) =>
    variant === 'outlined' &&
    css`
      background-color: transparent;
      color: white;
      border: 2px solid rgba(255, 255, 255, 0.7);
      box-shadow: none;
      
      &:hover {
        background-color: rgba(72, 149, 239, 0.15);
        border-color: white;
        transform: translateY(-1px);
      }
      
      &:active {
        background-color: rgba(72, 149, 239, 0.25);
        transform: translateY(0);
      }
      
      &:disabled {
        border-color: ${theme.colors.border};
        color: ${theme.colors.textLight};
        cursor: not-allowed;
        transform: none;
      }
    `}

  ${({ variant, theme }) =>
    variant === 'outlined-grey' &&
    css`
      background-color: transparent;
      color: ${theme.colors.textLight};
      border: 1px solid ${theme.colors.border};
      box-shadow: none;
      padding-top: calc(${theme.spacing[1]} + 1px);
      padding-bottom: calc(${theme.spacing[1]} + 1px);

      &:hover {
        background-color: rgba(0, 0, 0, 0.03);
        color: ${theme.colors.text};
        border-color: ${theme.colors.textLight};
      }
      
      &:active {
        background-color: rgba(0, 0, 0, 0.05);
        transform: translateY(0);
      }
      
      &:disabled {
        border-color: ${theme.colors.border};
        color: ${theme.colors.textLight};
        background-color: transparent;
        cursor: not-allowed;
        transform: none;
      }
    `}
`;

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  disabled = false,
  onClick,
  type = 'button',
  children,
  as,
  ...props
}) => {
  return (
    <StyledButton
      as={as}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      onClick={onClick}
      type={type}
      {...props}
    >
      {children}
    </StyledButton>
  );
};

export default Button; 